import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Clock3, Loader2, Send, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const when = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

// Footer widget on a supplier costing: shows approval status and lets Sales ask Ronick to approve.
export const CostingApproval = ({ rfqId, supplierId, beforeRequest }) => {
  const [a, setA] = useState(undefined);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/rfqs/${rfqId}/suppliers/${supplierId}/costing/approval`).then(({ data }) => setA(data)).catch(() => setA(null));
  }, [rfqId, supplierId]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    setBusy(true);
    try {
      if (beforeRequest) await beforeRequest();
      const { data } = await api.post(`/rfqs/${rfqId}/suppliers/${supplierId}/costing/approval`, { note });
      setA(data); setOpen(false); setNote("");
      toast.success("Sent to Ronick for approval");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (a === undefined) return null;
  const chip = !a ? null
    : a.status === "pending" ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1B4383]" data-testid="costing-approval-status"><Clock3 className="h-3.5 w-3.5" />Awaiting Approval · {when(a.requested_at)}</span>
    : a.status === "approved" ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1B4383]" data-testid="costing-approval-status"><CheckCircle2 className="h-3.5 w-3.5" />Approved by {a.decided_by_name}{a.new_margin_pct != null ? ` at ${a.new_margin_pct}% margin` : ""} · {when(a.decided_at)}</span>
    : <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700" data-testid="costing-approval-status"><Undo2 className="h-3.5 w-3.5" />Sent Back{a.new_margin_pct != null ? ` (margin set to ${a.new_margin_pct}%)` : ""}: {a.decision_note}</span>;
  return (
    <div className="mr-auto flex flex-wrap items-center gap-3">
      {chip}
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="costing-request-approval"><Send className="mr-2 h-4 w-4" />{a && a.status !== "sent_back" ? "Resend Request" : "Request Approval"}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="costing-approval-dialog">
          <DialogHeader><DialogTitle>Request Costing Approval</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">The costing is saved and Ronick sees a summary in Approvals.</p>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for Ronick (optional)" data-testid="costing-approval-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={send} disabled={busy} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="costing-approval-send">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send For Approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
